"use client";

import { type JSX, type ReactNode } from "react";
import {
  AtomicLayoutSection,
  AtomicQueryError,
  AtomicResultList,
} from "@coveo/atomic-react";
import type { Result, SearchEngine } from "@coveo/headless";
import { ClientOnly } from "../../../atoms/ClientOnly";
import Loader from "../../../atoms/Loader";
import { type Density, type Display, type ImageSize } from "types/Search";
import CustomNoResult from "./CustomNoResult";
import { useSearchResultsLoading } from "./useSearchResultsState";

interface SearchResultsShellProps {
  isPageEditing: boolean;
  searchEngine: SearchEngine;
  id?: string;
  containerClassName: string;
  dataRole: string;
  ariaLabel?: string;
  display: Display;
  density: Density;
  imageSize: ImageSize;
  template: (result: Result) => JSX.Element;
  resultsNotFoundText: string;
  /**
   * Optional extra `<style>` block injected just before the result list —
   * Solutions / Offerings need a grid stylesheet that targets `::part()`
   * selectors on the list itself, not the individual result template.
   */
  resultListStyles?: string;
  /**
   * Optional className wrapping `<AtomicResultList>` (used by the grid
   * variants to constrain card width).
   */
  resultListWrapperClassName?: string;
}

// Wraps every SearchResults variant. Editing mode shows a single hint;
// preview/published mode renders the Coveo result-list pipeline.
export const SearchResultsShell = ({
  isPageEditing,
  searchEngine,
  id,
  containerClassName,
  dataRole,
  ariaLabel,
  display,
  density,
  imageSize,
  template,
  resultsNotFoundText,
  resultListStyles,
  resultListWrapperClassName,
}: SearchResultsShellProps): JSX.Element => {
  const isLoading = useSearchResultsLoading(searchEngine);

  const resultList = (
    <AtomicResultList
      display={display}
      density={density}
      imageSize={imageSize}
      template={template}
    />
  );

  const wrappedResultList = resultListWrapperClassName ? (
    <div className={resultListWrapperClassName}>{resultList}</div>
  ) : (
    resultList
  );

  return (
    <div
      id={id ?? undefined}
      className={containerClassName}
      data-role={dataRole}
      aria-label={ariaLabel}
    >
      <ClientOnly>
        {isPageEditing ? (
          renderEditingHint()
        ) : (
          <AtomicLayoutSection section="main">
            <AtomicLayoutSection section="results">
              {isLoading && <Loader />}
              {resultListStyles && <style>{resultListStyles}</style>}
              {wrappedResultList}
              <AtomicQueryError />
              <CustomNoResult
                engine={searchEngine}
                resultsNotFoundText={resultsNotFoundText}
              />
            </AtomicLayoutSection>
          </AtomicLayoutSection>
        )}
      </ClientOnly>
    </div>
  );
};

const renderEditingHint = (): ReactNode => (
  <div className="is-metadata-hint">
    <strong>Search:</strong> results
  </div>
);
